import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Shield, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState, useEffect } from 'react';
import { fixImageUrl } from '../utils/imageUtils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HomePage() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const defaultHeroImage = "https://images.unsplash.com/photo-1520608421741-68228b76b6df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjB2aWxsYSUyMGV4dGVyaW9yJTIwYmVsZ2l1bSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Njk4NTU1MjR8MA&ixlib=rb-4.1.0&q=85";
  const defaultAboutImage = "https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjByZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3Njk4NTU1MzB8MA&ixlib=rb-4.1.0&q=85";

  // Fix image URLs for proper routing
  const heroImage = fixImageUrl(settings?.hero_image_url) || defaultHeroImage;
  const aboutImage = fixImageUrl(settings?.about_image_url) || defaultAboutImage;

  return (
    <div data-testid="home-page" className="min-h-screen overflow-x-hidden">
      <section className="hero-section">
        <img
          src={heroImage}
          alt="Luxury Belgian Real Estate"
          className="hero-image"
        />
        <div className="hero-overlay" />
        <div className="hero-content min-h-[90vh] flex items-center">
          <div className="px-4 md:px-12 lg:px-24 py-24 md:py-32 max-w-4xl">
            <div className="space-y-6 md:space-y-8 fade-in">
              <h1 data-testid="hero-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-serif tracking-tight leading-tight text-white">
                {settings?.hero_title || "Votre partenaire immobilier de confiance en Belgique"}
              </h1>
              <p className="text-sm md:text-base lg:text-lg leading-relaxed text-white/90 max-w-2xl">
                {settings?.hero_subtitle || "ARYA REAL ESTATE vous accompagne dans tous vos projets immobiliers avec professionnalisme et expertise."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4">
                <Link to="/properties">
                  <Button data-testid="hero-cta-properties" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 md:px-8 py-4 md:py-6 text-xs md:text-sm uppercase tracking-widest font-semibold transition-all duration-300 btn-primary">
                    Découvrir nos biens
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/rental-management">
                  <Button data-testid="hero-cta-rental" className="w-full sm:w-auto bg-transparent border border-white text-white hover:bg-white hover:text-foreground rounded-full px-6 md:px-8 py-4 md:py-6 text-xs md:text-sm uppercase tracking-widest transition-all duration-300">
                    Gestion locative
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="about-section" className="py-16 md:py-24 lg:py-32 px-4 md:px-12 lg:px-24 grain-texture">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-4 md:space-y-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Qui sommes-nous?</div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight leading-tight">
                {settings?.about_title || "Une agence immobilière moderne et humaine"}
              </h2>
              <p className="text-sm md:text-base lg:text-lg leading-relaxed text-muted-foreground">
                {settings?.about_description || "Fondée sur des valeurs de confiance et d'excellence, ARYA REAL ESTATE est votre partenaire idéal pour tous vos projets immobiliers en Belgique. Notre équipe d'experts vous accompagne à chaque étape."}
              </p>
              <p className="text-sm md:text-base lg:text-lg leading-relaxed text-muted-foreground">
                Que vous cherchiez à acheter, vendre ou gérer votre patrimoine immobilier, nous mettons notre expertise et notre réseau à votre service pour garantir votre succès.
              </p>
            </div>
            <div className="relative">
              <img
                src={aboutImage}
                alt="ARYA Real Estate Team"
                className="w-full max-w-xs md:max-w-sm shadow-xl mx-auto md:ml-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section data-testid="features-section" className="py-16 md:py-24 lg:py-32 px-4 md:px-12 lg:px-24 bg-secondary">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight mb-4">Nos services</h2>
            <p className="text-sm md:text-base lg:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              Une gamme complète de services pour répondre à tous vos besoins immobiliers
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            <div data-testid="feature-card-1" className="bg-white border border-border p-6 md:p-8 space-y-3 md:space-y-4 hover:shadow-lg transition-all duration-300">
              <Building2 className="w-10 h-10 md:w-12 md:h-12 text-primary" />
              <h3 className="text-xl md:text-2xl font-serif tracking-tight">{settings?.service1_title || "Achat & Vente"}</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {settings?.service1_description || "Trouvez le bien idéal ou vendez votre propriété au meilleur prix avec notre accompagnement personnalisé."}
              </p>
            </div>

            <div data-testid="feature-card-2" className="bg-white border border-border p-6 md:p-8 space-y-3 md:space-y-4 hover:shadow-lg transition-all duration-300">
              <Users className="w-10 h-10 md:w-12 md:h-12 text-primary" />
              <h3 className="text-xl md:text-2xl font-serif tracking-tight">{settings?.service2_title || "Gestion Locative"}</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {settings?.service2_description || "Confiez-nous la gestion de vos biens locatifs pour une tranquillité d'esprit totale."}
              </p>
            </div>

            <div data-testid="feature-card-3" className="bg-white border border-border p-6 md:p-8 space-y-3 md:space-y-4 hover:shadow-lg transition-all duration-300">
              <Shield className="w-10 h-10 md:w-12 md:h-12 text-primary" />
              <h3 className="text-xl md:text-2xl font-serif tracking-tight">{settings?.service3_title || "Expertise"}</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {settings?.service3_description || "Bénéficiez de notre expertise du marché belge et de nos conseils avisés."}
              </p>
            </div>

            <div data-testid="feature-card-4" className="bg-white border border-border p-6 md:p-8 space-y-3 md:space-y-4 hover:shadow-lg transition-all duration-300">
              <Award className="w-10 h-10 md:w-12 md:h-12 text-primary" />
              <h3 className="text-xl md:text-2xl font-serif tracking-tight">{settings?.service4_title || "Excellence"}</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {settings?.service4_description || "Un service de qualité supérieure pour garantir votre satisfaction à chaque étape."}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="py-16 md:py-24 lg:py-32 px-4 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight">
            Prêt à commencer votre projet immobilier?
          </h2>
          <p className="text-sm md:text-base lg:text-lg leading-relaxed text-muted-foreground">
            Contactez-nous dès aujourd'hui pour discuter de vos besoins et découvrir comment nous pouvons vous aider.
          </p>
          <Link to="/properties">
            <Button data-testid="cta-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 md:px-8 py-4 md:py-6 text-xs md:text-sm uppercase tracking-widest font-semibold transition-all duration-300 btn-primary">
              Voir nos biens disponibles
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default HomePage;